import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';

import {
  extractComplianceMatrix,
  runNliRedTeam,
  saveAssessment,
} from '../../services/procurementService';
import type {
  Assessment,
  Procurement,
  ProcurementSettings,
  Supplier,
} from '../../types/procurement';

interface Props {
  procurementId: string;
  procurement: Procurement;
  suppliers: Supplier[];
  assessments?: Assessment[];
  onSaveSettings: (settings: ProcurementSettings) => void;
  onShowSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const PreEvaluationView: React.FC<Props> = ({
  procurementId,
  procurement,
  suppliers,
  assessments = [],
  onSaveSettings,
  onShowSnackbar,
}) => {
  const { account } = useAccount();
  const [isRunningCompliance, setIsRunningCompliance] = useState(false);
  const [isRunningNli, setIsRunningNli] = useState(false);

  // Mock state for the UI, in reality this would come from Firestore
  const [complianceResults, setComplianceResults] = useState<
    Record<string, Record<string, { status: string; reason?: string }>>
  >({});
  const [nliResults, setNliResults] = useState<
    Record<string, { risk: string; description: string; citations?: string[] }[]>
  >({});

  useEffect(() => {
    const compResults: Record<string, Record<string, { status: string; reason?: string }>> = {};
    const nliRes: Record<string, { risk: string; description: string; citations?: string[] }[]> =
      {};

    for (const assessment of assessments) {
      if (assessment.type === 'COMPLIANCE_CHECK' && assessment.results) {
        if (!compResults[assessment.supplierId]) {
          compResults[assessment.supplierId] = {};
        }
        for (const res of assessment.results) {
          const supplierObj = compResults[assessment.supplierId];
          if (supplierObj) {
            supplierObj[res.requirement] = {
              status: res.met ? 'PASS' : 'FAIL',
              reason: res.evidence,
            };
          }
        }
      } else if (assessment.type === 'NLI_SIMULATION' && assessment.contradictions) {
        nliRes[assessment.supplierId] = assessment.contradictions;
      }
    }

    setComplianceResults(compResults);
    setNliResults(nliRes);
  }, [assessments]);

  const handleRunCompliance = async () => {
    if (!account?.clientId) {
      return;
    }
    if (suppliers.length === 0) {
      onShowSnackbar?.('No suppliers available to analyze.', 'error');
      return;
    }
    setIsRunningCompliance(true);
    try {
      const promises = suppliers.map(async (supplier) => {
        // We only want to skip documents that are actively being processed or are known to be corrupt.
        const documentPaths = (supplier.documents || [])
          .filter((d) => {
            const isValid =
              d.url &&
              d.status !== 'pending' &&
              d.status !== 'corrupt' &&
              d.conversionStatus !== 'pending' &&
              d.conversionStatus !== 'processing';
            return isValid;
          })
          .map((d) => d.url);

        if (documentPaths.length === 0) {
          return {
            supplierId: supplier.id,
            success: false,
            error: 'No valid documents available.',
          };
        }

        const result = await extractComplianceMatrix(
          procurementId,
          account.clientId,
          account.uid,
          supplier.id,
          documentPaths,
          procurement.complianceRequirements || [],
          currentSettings
        );

        if (result.success && result.compliance_matrix) {
          // Format matrix back to assessment array format
          const formattedResults = Object.entries(result.compliance_matrix).map(
            ([req, status]) => ({
              requirement: req,
              met: status === 'PASS',
              reason: status === 'PASS' ? '' : status.replace('FAIL - ', ''),
            })
          );

          // Persist to Firestore
          const existingAssessment = assessments.find(
            (a) => a.supplierId === supplier.id && a.type === 'COMPLIANCE_CHECK'
          );

          await saveAssessment(
            procurementId,
            existingAssessment?.id || crypto.randomUUID(),
            supplier.id,
            'COMPLIANCE_CHECK',
            { results: formattedResults, status: 'COMPLETED' }
          );

          return { supplierId: supplier.id, success: true, matrix: result.compliance_matrix };
        }
        return { supplierId: supplier.id, success: false, error: result.error };
      });

      const results = await Promise.allSettled(promises);
      const newResults = { ...complianceResults };
      let successCount = 0;
      let failCount = 0;
      const errorMessages: string[] = [];

      for (const res of results) {
        if (res.status === 'fulfilled' && res.value.success && res.value.matrix) {
          const formattedMatrix: Record<string, { status: string; reason?: string }> = {};
          for (const [req, statusStr] of Object.entries(res.value.matrix)) {
            let status = 'FAIL';
            let reason = statusStr;

            if (typeof statusStr === 'string') {
              if (statusStr === 'PASS') {
                status = 'PASS';
                reason = '';
              } else if (statusStr.startsWith('UNKNOWN')) {
                status = 'UNKNOWN';
                reason = statusStr.replace('UNKNOWN - ', '').replace('UNKNOWN', '');
              } else {
                status = 'FAIL';
                reason = statusStr.replace('FAIL - ', '').replace('FAIL', '');
              }
            }

            formattedMatrix[req] = {
              status,
              reason,
            };
          }
          newResults[res.value.supplierId] = formattedMatrix;
          successCount++;
        } else {
          failCount++;
          if (res.status === 'rejected') {
            errorMessages.push(res.reason?.message || String(res.reason));
          } else if (res.status === 'fulfilled' && !res.value.success) {
            errorMessages.push(res.value.error || 'Unknown error');
          }
        }
      }

      setComplianceResults(newResults);

      if (successCount > 0 && failCount === 0) {
        onShowSnackbar?.('Compliance extraction complete for all suppliers.', 'success');
      } else if (successCount > 0 && failCount > 0) {
        onShowSnackbar?.(
          `Compliance extraction completed with some failures. Errors: ${errorMessages.join(', ')}`,
          'error'
        );
      } else {
        const uniqueErrors = Array.from(new Set(errorMessages));
        onShowSnackbar?.(
          `Compliance extraction failed for all suppliers. Reason: ${uniqueErrors.join(' | ')}`,
          'error'
        );
      }
    } catch (err: unknown) {
      const e = err as Error;
      onShowSnackbar?.(e.message || 'Compliance extraction failed.', 'error');
    } finally {
      setIsRunningCompliance(false);
    }
  };

  const handleRunNli = async () => {
    if (!account?.clientId) {
      return;
    }
    if (suppliers.length === 0) {
      onShowSnackbar?.('No suppliers available to analyze.', 'error');
      return;
    }
    setIsRunningNli(true);
    try {
      const promises = suppliers.map(async (supplier) => {
        // We only want to skip documents that are actively being processed or are known to be corrupt.
        const documentPaths = (supplier.documents || [])
          .filter((d) => {
            const isValid =
              d.url &&
              d.status !== 'pending' &&
              d.status !== 'corrupt' &&
              d.conversionStatus !== 'pending' &&
              d.conversionStatus !== 'processing';
            return isValid;
          })
          .map((d) => d.url);

        if (documentPaths.length === 0) {
          return {
            supplierId: supplier.id,
            success: false,
            error: 'No valid documents available.',
          };
        }

        const result = await runNliRedTeam(
          procurementId,
          account.clientId,
          account.uid,
          supplier.id,
          documentPaths,
          currentSettings
        );

        if (result.success && result.contradictions) {
          // Persist to Firestore
          const existingAssessment = assessments.find(
            (a) => a.supplierId === supplier.id && a.type === 'NLI_SIMULATION'
          );

          await saveAssessment(
            procurementId,
            existingAssessment?.id || crypto.randomUUID(),
            supplier.id,
            'NLI_SIMULATION',
            { contradictions: result.contradictions, status: 'COMPLETED' }
          );

          return { supplierId: supplier.id, success: true, contradictions: result.contradictions };
        }
        return { supplierId: supplier.id, success: false, error: result.error };
      });

      const results = await Promise.allSettled(promises);
      const newResults = { ...nliResults };
      let successCount = 0;
      let failCount = 0;
      const errorMessages: string[] = [];

      for (const res of results) {
        if (res.status === 'fulfilled' && res.value.success && res.value.contradictions) {
          newResults[res.value.supplierId] = res.value.contradictions;
          successCount++;
        } else {
          failCount++;
          if (res.status === 'rejected') {
            errorMessages.push(res.reason?.message || String(res.reason));
          } else if (res.status === 'fulfilled' && !res.value.success) {
            errorMessages.push(res.value.error || 'Unknown error');
          }
        }
      }

      setNliResults(newResults);

      if (successCount > 0 && failCount === 0) {
        onShowSnackbar?.('Contradiction detection complete for all suppliers.', 'success');
      } else if (successCount > 0 && failCount > 0) {
        onShowSnackbar?.(
          `Contradiction detection completed with some failures. Errors: ${errorMessages.join(', ')}`,
          'error'
        );
      } else {
        const uniqueErrors = Array.from(new Set(errorMessages));
        onShowSnackbar?.(
          `Contradiction detection failed for all suppliers. Reason: ${uniqueErrors.join(' | ')}`,
          'error'
        );
      }
    } catch (err: unknown) {
      const e = err as Error;
      onShowSnackbar?.(e.message || 'Contradiction detection failed.', 'error');
    } finally {
      setIsRunningNli(false);
    }
  };

  const allRequirements = useMemo(() => {
    const reqs = new Set<string>();
    for (const matrix of Object.values(complianceResults)) {
      for (const req of Object.keys(matrix)) {
        reqs.add(req);
      }
    }
    return Array.from(reqs);
  }, [complianceResults]);

  const renderComplianceChip = (value: { status: string; reason?: string }) => {
    if (value.status === 'PASS') {
      return (
        <Tooltip title="PASS">
          <CheckCircleOutlineIcon color="success" />
        </Tooltip>
      );
    }
    if (value.status === 'UNKNOWN') {
      return (
        <Tooltip title={value.reason || 'UNKNOWN'}>
          <WarningAmberIcon color="warning" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={value.reason || 'FAIL'}>
        <ErrorOutlineIcon color="error" />
      </Tooltip>
    );
  };

  const renderRiskChip = (risk: string) => {
    switch (risk) {
      case 'HIGH':
        return <Chip size="small" color="error" label="HIGH RISK" />;
      case 'MEDIUM':
        return <Chip size="small" color="warning" label="MEDIUM RISK" />;
      case 'LOW':
        return <Chip size="small" color="info" label="LOW RISK" />;
      default:
        return <Chip size="small" label={risk} />;
    }
  };

  const defaultSettings: ProcurementSettings = {
    compliance: { strictness: 'MEDIUM', customKeywords: [] },
    nliRedTeam: { enabled: true, confidenceThreshold: 'MEDIUM' },
    evaluation: { useFuzzyScoring: true, enabled: true },
    moderation: { reportFormat: 'EXECUTIVE_SUMMARY', tone: 'FORMAL' },
  };

  const currentSettings = procurement?.settings || defaultSettings;

  const handleUpdateSetting = (
    category: 'compliance' | 'nliRedTeam',
    field: string,
    value: string | boolean
  ) => {
    onSaveSettings({
      ...currentSettings,
      [category]: {
        ...currentSettings[category],
        [field]: value,
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5">Compliance & Triage</Typography>
      <Typography variant="body1" color="textSecondary">
        Run automated AI pipelines to scan supplier documents for mandatory compliance requirements
        and flag internal contradictions or semantic risks before human evaluators begin their
        assessment.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Compliance Results */}
        <Card>
          <CardHeader
            sx={{ bgcolor: 'action.hover' }}
            title="Compliance Results"
            subheader="Pass/Fail extraction from mandatory documents"
            action={
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Strictness"
                  sx={{ minWidth: 150, bgcolor: 'background.paper' }}
                  value={currentSettings.compliance.strictness}
                  onChange={(e) => handleUpdateSetting('compliance', 'strictness', e.target.value)}
                >
                  <MenuItem value="LOW">Low</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="HIGH">High</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={
                    isRunningCompliance ? <CircularProgress size={16} /> : <AutoAwesomeIcon />
                  }
                  onClick={handleRunCompliance}
                  disabled={isRunningCompliance || suppliers.length === 0}
                  data-testid="run-compliance-btn"
                >
                  Run Compliance Check
                </Button>
              </Box>
            }
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            {Object.keys(complianceResults).length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                No compliance data available. Run the check to extract.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          minWidth: 150,
                          maxWidth: 200,
                          bgcolor: 'background.default',
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          borderRight: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        Supplier
                      </TableCell>
                      {allRequirements.map((req) => (
                        <TableCell
                          key={req}
                          align="center"
                          sx={{
                            fontWeight: 'bold',
                            minWidth: 120,
                            maxWidth: 160,
                            bgcolor: 'background.default',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            lineHeight: 1.2,
                          }}
                        >
                          {req}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.map((sup) => {
                      const matrix = complianceResults[sup.id];
                      if (!matrix) {
                        return null;
                      }
                      return (
                        <TableRow key={sup.id} hover>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              bgcolor: 'background.paper',
                              borderRight: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {sup.name}
                          </TableCell>
                          {allRequirements.map((req) => {
                            const result = matrix[req];
                            return (
                              <TableCell
                                key={`${sup.id}-${req}`}
                                align="center"
                                sx={{
                                  bgcolor: (theme) => {
                                    if (!result) {
                                      return 'inherit';
                                    }
                                    if (result.status === 'PASS') {
                                      return alpha(theme.palette.success.main, 0.1);
                                    }
                                    if (result.status === 'UNKNOWN') {
                                      return alpha(theme.palette.warning.main, 0.15);
                                    }
                                    if (result.status === 'FAIL') {
                                      return alpha(theme.palette.error.main, 0.1);
                                    }
                                    return 'inherit';
                                  },
                                }}
                              >
                                {result ? (
                                  renderComplianceChip(result)
                                ) : (
                                  <Typography variant="caption" color="textSecondary">
                                    N/A
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Contradiction Detector */}
        <Card>
          <CardHeader
            sx={{ bgcolor: 'action.hover' }}
            title="Contradiction Detector"
            subheader="Semantic contradiction detection across bids"
            action={
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentSettings.nliRedTeam.enabled}
                      onChange={(e) =>
                        handleUpdateSetting('nliRedTeam', 'enabled', e.target.checked)
                      }
                    />
                  }
                  label="Enabled"
                  sx={{ m: 0 }}
                />
                <TextField
                  select
                  size="small"
                  label="Confidence"
                  sx={{ minWidth: 150, bgcolor: 'background.paper' }}
                  disabled={!currentSettings.nliRedTeam.enabled}
                  value={currentSettings.nliRedTeam.confidenceThreshold}
                  onChange={(e) =>
                    handleUpdateSetting('nliRedTeam', 'confidenceThreshold', e.target.value)
                  }
                >
                  <MenuItem value="LOW">All Risks</MenuItem>
                  <MenuItem value="MEDIUM">Med/High Risks</MenuItem>
                  <MenuItem value="HIGH">High Risks</MenuItem>
                </TextField>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={isRunningNli ? <CircularProgress size={16} /> : <WarningAmberIcon />}
                  onClick={handleRunNli}
                  disabled={
                    isRunningNli || suppliers.length === 0 || !currentSettings.nliRedTeam.enabled
                  }
                  data-testid="run-nli-btn"
                >
                  Detect Contradictions
                </Button>
              </Box>
            }
          />
          <Divider />
          <CardContent sx={{ bgcolor: 'background.default' }}>
            {Object.keys(nliResults).length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
                No contradiction data available. Run the simulation to detect risks.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {suppliers.map((sup) => {
                  const contradictions = nliResults[sup.id];
                  if (!contradictions || contradictions.length === 0) {
                    return null;
                  }
                  return (
                    <Card
                      key={`nli-${sup.id}`}
                      variant="outlined"
                      sx={{ bgcolor: 'background.paper' }}
                    >
                      <CardHeader
                        title={sup.name}
                        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
                        sx={{ bgcolor: 'action.hover', py: 1 }}
                      />
                      <Divider />
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
                        {contradictions.map((c) => (
                          <Box
                            key={c.description.substring(0, 30)}
                            sx={{
                              display: 'flex',
                              gap: 2,
                              alignItems: 'flex-start',
                              p: 1.5,
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ flexShrink: 0 }}>{renderRiskChip(c.risk)}</Box>
                            <Box sx={{ pt: 0.5, flexGrow: 1 }}>
                              <Typography variant="body2">{c.description}</Typography>
                              {c.citations && c.citations.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="textSecondary"
                                  >
                                    References:
                                  </Typography>
                                  <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                                    {c.citations.map((citation, idx) => {
                                      const key = `${citation.substring(0, 10)}-${idx}`;
                                      return (
                                        <Typography
                                          component="li"
                                          key={key}
                                          variant="caption"
                                          color="textSecondary"
                                          sx={{ mb: 0.5, fontStyle: 'italic' }}
                                        >
                                          "{citation}"
                                        </Typography>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PreEvaluationView;
